import { Chain } from '@ankr.com/chains-list';
import { GetUptimeApiResponse, UptimeRange } from 'multirpc-sdk';
import { useMemo } from 'react';

import { useAppSelector } from 'store/useAppSelector';
import { selectAllPathsByChainId } from 'modules/chains/store/selectors';
import { useFetchUptimeQuery } from 'modules/status/actions/fetchUptime';
import { calculateChainUptime } from 'domains/status/utils/calculateChainUptime';

const DEFAULT_RANGE: UptimeRange = '24h';

export const useChainStatuses = (chain: Chain, isLoggedIn: boolean) => {
  const currentChainPaths = useAppSelector(state =>
    selectAllPathsByChainId(state, chain.id),
  );

  const {
    data: uptimeData,
    isFetching: isFetchingUptime,
    isLoading: isUptimeLoading,
  } = useFetchUptimeQuery(
    {
      range: DEFAULT_RANGE,
    },
    {
      skip: !isLoggedIn,
    },
  );

  const currentChainStatuses = useMemo(() => {
    if (!uptimeData || isUptimeLoading) return [];

    // Filter uptimeData to only include entries matching currentChainPaths
    const filteredUptimes: GetUptimeApiResponse = Object.fromEntries(
      Object.entries(uptimeData).filter(([chainId]) =>
        currentChainPaths.includes(chainId),
      ),
    );

    const chainsUptime = calculateChainUptime(filteredUptimes);

    return chainsUptime;
  }, [currentChainPaths, isUptimeLoading, uptimeData]);

  const hasChainStatus = currentChainStatuses.length > 0;

  return {
    hasChainStatus,
    currentChainStatuses,
    isLoading: isUptimeLoading,
    isFetching: isFetchingUptime,
  };
};
